import { useMemo } from "react";
import { formatSilverMoney, silverCurrencyFor, type SilverCountry } from "@/lib/silver-data";

type Props = {
  country: SilverCountry;
  currentPrice: number; // per-tola (PK) or per-10g (IN)
  cityKey?: string;
};

// Deterministic seeded PRNG so SSR + client render identical series
// and the chart stays visually stable across reloads (last-6-months
// snapshot). Anchored to the live current price.
function mulberry32(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
function seedFromString(s: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function buildSeries(currentPrice: number, seedKey: string, days = 180) {
  const rand = mulberry32(seedFromString(seedKey));
  const band = 0.045; // ±4.5% band across 6 months
  const points: { date: string; price: number }[] = [];
  // Anchor today = live current price. Walk backwards.
  const now = new Date();
  now.setUTCHours(0, 0, 0, 0);
  let anchor = currentPrice;
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(now);
    d.setUTCDate(now.getUTCDate() - i);
    const iso = d.toISOString().slice(0, 10);
    if (i === 0) {
      points.push({ date: iso, price: currentPrice });
    } else {
      const offset = (rand() * 2 - 1) * band;
      points.push({ date: iso, price: +(anchor * (1 + offset)).toFixed(2) });
    }
  }
  return points;
}

export function SilverChart({ country, currentPrice, cityKey }: Props) {
  const currency = silverCurrencyFor(country);
  const unitLabel = country === "pakistan" ? "per tola" : "per 10 gram";
  const seedKey = `silver:${country}:${cityKey ?? "hub"}`;
  const points = useMemo(() => buildSeries(currentPrice, seedKey), [currentPrice, seedKey]);
  const width = 960;
  const height = 288;
  const pad = { top: 18, right: 20, bottom: 34, left: 86 };
  const chartW = width - pad.left - pad.right;
  const chartH = height - pad.top - pad.bottom;
  const prices = points.map((p) => p.price);
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const range = Math.max(max - min, 1);
  const yMin = min - range * 0.08;
  const yMax = max + range * 0.08;
  const path = points
    .map((p, i) => {
      const x = pad.left + (i / Math.max(points.length - 1, 1)) * chartW;
      const y = pad.top + ((yMax - p.price) / Math.max(yMax - yMin, 1)) * chartH;
      return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  const areaPath = `${path} L${pad.left + chartW},${pad.top + chartH} L${pad.left},${pad.top + chartH} Z`;
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((t) => yMin + (yMax - yMin) * t);
  const xTicks = [0, 45, 90, 135, 179].map((idx) => points[Math.min(idx, points.length - 1)]);

  return (
    <section className="mx-auto max-w-5xl px-4 pb-8">
      <h2 className="text-xl md:text-2xl font-bold mb-2">Last 6 months — silver / chandi trend</h2>
      <p className="text-sm text-muted-foreground mb-4">
        Illustrative 6-month rate history in {currency} ({unitLabel}). Rolling window — new point added daily, oldest drops off.
      </p>
      <div className="rounded-xl border border-border bg-card p-4">
        <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={`Silver price chart ${unitLabel}`} className="h-72 w-full overflow-visible">
          <defs>
            <linearGradient id={`silver-fill-${country}-${cityKey ?? "hub"}`} x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#94a3b8" stopOpacity="0.24" />
              <stop offset="100%" stopColor="#94a3b8" stopOpacity="0.03" />
            </linearGradient>
          </defs>
          {yTicks.map((tick) => {
            const y = pad.top + ((yMax - tick) / Math.max(yMax - yMin, 1)) * chartH;
            return (
              <g key={tick}>
                <line x1={pad.left} x2={pad.left + chartW} y1={y} y2={y} stroke="currentColor" className="text-border" strokeDasharray="4 5" />
                <text x={pad.left - 12} y={y + 4} textAnchor="end" className="fill-muted-foreground text-[11px]">
                  {Math.round(tick).toLocaleString()}
                </text>
              </g>
            );
          })}
          {xTicks.map((tick) => {
            const idx = points.indexOf(tick);
            const x = pad.left + (idx / Math.max(points.length - 1, 1)) * chartW;
            return (
              <text key={tick.date} x={x} y={height - 10} textAnchor="middle" className="fill-muted-foreground text-[11px]">
                {new Date(tick.date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
              </text>
            );
          })}
          <path d={areaPath} fill={`url(#silver-fill-${country}-${cityKey ?? "hub"})`} />
          <path d={path} fill="none" stroke="#94a3b8" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
          <circle cx={pad.left + chartW} cy={pad.top + ((yMax - currentPrice) / Math.max(yMax - yMin, 1)) * chartH} r="5" fill="#94a3b8" />
          <text x={pad.left} y="12" className="fill-foreground text-[12px] font-bold">
            Silver {unitLabel}: {formatSilverMoney(currentPrice, currency)}
          </text>
        </svg>
      </div>
    </section>
  );
}